import { useEffect } from 'react';

const BASE_TITLE = 'Reflex';

function isLocalHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

export function buildDocumentTitle(prefix: string | null | undefined, hostname: string): string {
  const local = isLocalHost(hostname);
  const trimmed = prefix?.trim();
  if (!trimmed) {
    return local ? `[local] ${BASE_TITLE}` : BASE_TITLE;
  }
  return local ? `[local] ${trimmed} · ${BASE_TITLE}` : `${trimmed} · ${BASE_TITLE}`;
}

/**
 * Set `document.title` to `${prefix} · Reflex` (preserving the `[local]`
 * marker on localhost) while the calling component is mounted, and reset
 * to the default Reflex title on unmount or when `prefix` becomes empty.
 *
 * Pass `null`/`undefined`/empty string to opt back into the default title
 * without unmounting — useful while a value is still loading.
 *
 * The cleanup only resets the title when `document.title` still matches the
 * value this effect installed. AnimatePresence keeps an exiting view in the
 * tree during its 150ms exit animation, so a sibling caller that mounted
 * after us may have already taken ownership of the title — without this
 * guard, our late-firing cleanup would clobber their value back to the
 * default.
 */
export function useDocumentTitle(prefix: string | null | undefined): void {
  useEffect(() => {
    const hostname = window.location.hostname;
    const next = buildDocumentTitle(prefix, hostname);
    document.title = next;
    return () => {
      if (document.title === next) {
        document.title = buildDocumentTitle(null, hostname);
      }
    };
  }, [prefix]);
}
